import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Input, Label, Textarea } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { createDomain, updateDomain } from "@/lib/api/endpoints";
import { qk } from "@/lib/query/keys";
import type { DomainView } from "@/types/api";

/** Split a textarea (newline- or comma-separated) into a clean list. */
function parseList(raw: string): string[] {
  const seen = new Set<string>();
  for (const part of raw.split(/[\n,]/)) {
    const v = part.trim();
    if (v) seen.add(v);
  }
  return [...seen];
}

/** Create (when `domain` is undefined) or edit a domain. Controlled dialog. */
export function DomainDialog({
  open,
  onOpenChange,
  domain,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domain?: DomainView;
}) {
  const qc = useQueryClient();
  const editing = !!domain;
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [knowledgeSource, setKnowledgeSource] = React.useState("");
  const [vectorNamespace, setVectorNamespace] = React.useState("");
  const [allowedIssuers, setAllowedIssuers] = React.useState("");
  const [allowedAudiences, setAllowedAudiences] = React.useState("");

  // Reset fields whenever the dialog opens for a (possibly different) target.
  React.useEffect(() => {
    if (open) {
      setName(domain?.name ?? "");
      setDescription(domain?.description ?? "");
      setKnowledgeSource(domain?.knowledge_source ?? "");
      setVectorNamespace(domain?.vector_index_namespace ?? "");
      setAllowedIssuers((domain?.allowed_issuers ?? []).join("\n"));
      setAllowedAudiences((domain?.allowed_audiences ?? []).join("\n"));
    }
  }, [open, domain]);

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description: description || null,
        knowledge_source: knowledgeSource.trim() || null,
        vector_index_namespace: vectorNamespace.trim() || null,
        allowed_issuers: parseList(allowedIssuers),
        allowed_audiences: parseList(allowedAudiences),
      };
      return editing ? updateDomain(domain!.slug, body) : createDomain(body);
    },
    onSuccess: async () => {
      toast.success(editing ? "Domain updated." : "Domain created.");
      await qc.invalidateQueries({ queryKey: qk.myDomains });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit domain" : "New domain"}</DialogTitle>
          <DialogDescription>
            A domain groups agents and capabilities, scopes their data, and controls which callers
            they answer. The slug is derived from the name.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) mutation.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="d-name">Name</Label>
            <Input
              id="d-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Support Desk"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="d-desc">Description</Label>
            <Textarea
              id="d-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this domain is for (optional)"
              rows={2}
            />
          </div>

          {/* Option A — outbound data scope. */}
          <fieldset className="space-y-3 rounded-md border border-border p-3">
            <legend className="px-1 text-xs font-medium text-muted-foreground">Data scope</legend>
            <p className="text-xs text-muted-foreground">
              Where this domain&apos;s data lives. Stamped as a <code>domain</code> claim on every
              tool/MCP call the agent makes, so downstream data servers can enforce it.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="d-knowledge">Knowledge source</Label>
              <Input
                id="d-knowledge"
                value={knowledgeSource}
                onChange={(e) => setKnowledgeSource(e.target.value)}
                placeholder="e.g. support-kb (optional)"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="d-vector">Vector index namespace</Label>
              <Input
                id="d-vector"
                value={vectorNamespace}
                onChange={(e) => setVectorNamespace(e.target.value)}
                placeholder="e.g. tenant-acme/support (optional)"
              />
            </div>
          </fieldset>

          {/* Option B — inbound access control. */}
          <fieldset className="space-y-3 rounded-md border border-border p-3">
            <legend className="px-1 text-xs font-medium text-muted-foreground">
              Access control
            </legend>
            <p className="text-xs text-muted-foreground">
              Restrict which verified callers this domain&apos;s agents answer. Leave blank to
              accept any trusted caller. One value per line. Enforced only when domain scoping is
              enabled for the environment.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="d-issuers">Allowed issuers (JWT&nbsp;iss)</Label>
              <Textarea
                id="d-issuers"
                value={allowedIssuers}
                onChange={(e) => setAllowedIssuers(e.target.value)}
                placeholder={"https://myfam.example\nhttps://partner.example"}
                rows={2}
                className="font-mono text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="d-audiences">Allowed audiences (JWT&nbsp;aud)</Label>
              <Textarea
                id="d-audiences"
                value={allowedAudiences}
                onChange={(e) => setAllowedAudiences(e.target.value)}
                placeholder="agent:support"
                rows={2}
                className="font-mono text-xs"
              />
            </div>
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || mutation.isPending}>
              {mutation.isPending ? "Saving…" : editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
